import { analyzeSpecs } from '~/config/settings/reporting.settings';
import { learningContentFrom } from './learning_content.transformer';
import { taskTemplateFrom } from './task_template.transformer';

/**
 * @module survey_reporting.transformer
 */

const overallResultFrom = (overallResult, surveyIds) => {
  const attributePrefix = 'overall-';
  const attributeResults = overallResult.attributeResults.map((attr) => attributeResultFrom(attr, attributePrefix));
  return {
    ...overallResult,
    id: surveyIds.join('-'),
    attributeResults,
  };
};

const attributeValueResultFrom = (attributeValueResult, prefix) => {
  return {
    id: prefix + attributeValueResult.attributeValue.split(' ').join(''),
    insight: attributeValueResult.insight,
    isAnonymized: attributeValueResult.isAnonymized,
    attributeValue: attributeValueResult.attributeValue,
    attributeValueLabel: attributeValueResult.attributeValueLabel,
    scaleDistribution: attributeValueResult.scaleDistribution,
    enpsDistribution: attributeValueResult.enpsDistribution,
    scoreAverage: attributeValueResult.scoreAverage,
    enpsScore: attributeValueResult.enpsScore,
    responseRate: attributeValueResult.responseRate,
    scaleRespondentCount: attributeValueResult.scaleRespondentCount,
    fullyAnonymizedRespondentCount: attributeValueResult.fullyAnonymizedRespondentCount,
    respondentPercent: attributeValueResult.responseRate
      ? attributeValueResult.responseRate.respondentCount / attributeValueResult.responseRate.recipientCount
      : null,
  };
};

const attributeResultFrom = (attributeResult, prefix) => {
  const attributePrefix =
    prefix + attributeResult.attributeId + '-' + attributeResult.isHierarchyAttribute.toString() + '-';
  const attributeValueResults = attributeResult.attributeValueResults.map((attrVal) => {
    return attributeValueResultFrom(attrVal, attributePrefix);
  });
  const minMaxStats = attributeValueResults.reduce(
    (stats, valueResult) => {
      const maxRespondentPercent =
        valueResult.respondentPercent !== null && valueResult.respondentPercent > stats.maxRespondentPercent
          ? valueResult.respondentPercent
          : stats.maxRespondentPercent;
      const minRespondentPercent =
        valueResult.respondentPercent !== null && valueResult.respondentPercent < stats.minRespondentPercent
          ? valueResult.respondentPercent
          : stats.minRespondentPercent;
      const maxScaleFavorable =
        valueResult.scaleDistribution?.favorablePercent > stats.maxScaleFavorable
          ? valueResult.scaleDistribution?.favorablePercent
          : stats.maxScaleFavorable;
      const minScaleFavorable =
        valueResult.scaleDistribution?.favorablePercent < stats.minScaleFavorable
          ? valueResult.scaleDistribution?.favorablePercent
          : stats.minScaleFavorable;
      const maxEnpsFavorable =
        valueResult.enpsDistribution?.favorablePercent > stats.maxEnpsFavorable
          ? valueResult.enpsDistribution?.favorablePercent
          : stats.maxEnpsFavorable;
      const minEnpsFavorable =
        valueResult.enpsDistribution?.favorablePercent < stats.minEnpsFavorable
          ? valueResult.enpsDistribution?.favorablePercent
          : stats.minEnpsFavorable;
      const maxScoreAverage =
        valueResult.scoreAverage > stats.maxScoreAverage ? valueResult.scoreAverage : stats.maxScoreAverage;
      const minScoreAverage =
        valueResult.scoreAverage !== null && valueResult.scoreAverage < stats.minScoreAverage
          ? valueResult.scoreAverage
          : stats.minScoreAverage;
      const maxEnpsScore = valueResult.enpsScore > stats.maxEnpsScore ? valueResult.enpsScore : stats.maxEnpsScore;
      const minEnpsScore = valueResult.enpsScore < stats.minEnpsScore ? valueResult.enpsScore : stats.minEnpsScore;
      return {
        maxRespondentPercent,
        minRespondentPercent,
        maxScaleFavorable,
        minScaleFavorable,
        maxEnpsFavorable,
        minEnpsFavorable,
        maxScoreAverage,
        minScoreAverage,
        maxEnpsScore,
        minEnpsScore,
      };
    },
    {
      maxRespondentPercent: 0,
      minRespondentPercent: 1,
      maxScaleFavorable: 0,
      minScaleFavorable: 1,
      maxEnpsFavorable: -1,
      minEnpsFavorable: 1,
      maxScoreAverage: 0,
      minScoreAverage: 1,
      maxEnpsScore: -1,
      minEnpsScore: 1,
    },
  );

  return {
    id: prefix + attributeResult.attributeId + '-' + attributeResult.isHierarchyAttribute.toString(),
    employeeAttribute: attributeResult.attributeId,
    isHierarchyAttribute: attributeResult.isHierarchyAttribute,
    attributeValueResults,
    ...minMaxStats,
  };
};

const surveyElementResultFrom = (surveyElementResult, prefix) => {
  const surveyElementPrefix = prefix + surveyElementResult.surveyElementId + '-';
  const attributeResults = surveyElementResult.attributeResults.map((attr) =>
    attributeResultFrom(attr, surveyElementPrefix),
  );

  // backend can't properly calculate this for now, so manually check it here
  const hasEnpsResult = !!surveyElementResult.enpsDistribution;

  return {
    id: prefix + surveyElementResult.surveyElementId,
    surveyElement: surveyElementResult.surveyElementId,
    isAnonymized: surveyElementResult.isAnonymized,
    isKey: surveyElementResult.isKey,
    scaleDistribution: !hasEnpsResult ? surveyElementResult.scaleDistribution : null,
    scoreAverage: surveyElementResult.scoreAverage,
    enpsDistribution: hasEnpsResult ? surveyElementResult.enpsDistribution : null,
    enpsScore: surveyElementResult.enpsScore,
    insight: surveyElementResult.insight,
    attributeResults,
  };
};

const categoryResultFrom = (categoryResult) => {
  const categoryPrefix = categoryResult.categoryId + '-';
  const surveyElementResults = categoryResult.surveyElementResults.map((surveyElementResult) =>
    surveyElementResultFrom(surveyElementResult, categoryPrefix),
  );
  // backend can't properly calculate this for now, so manually check it here
  const hasScaleResult = surveyElementResults.some((result) => !!result.scaleDistribution);
  const hasEnpsResult = surveyElementResults.some((result) => !!result.enpsDistribution);
  const attributeResults = categoryResult.attributeResults.map((attr) => attributeResultFrom(attr, categoryPrefix));

  return {
    id: categoryResult.categoryId,
    category: categoryResult.categoryId,
    isAnonymized: categoryResult.isAnonymized,
    isKey: categoryResult.isKey,
    scaleDistribution: hasScaleResult ? categoryResult.scaleDistribution : null,
    scoreAverage: categoryResult.scoreAverage,
    enpsDistribution: hasEnpsResult ? categoryResult.enpsDistribution : null,
    enpsScore: categoryResult.enpsScore,
    insight: categoryResult.insight,
    surveyElementResults,
    attributeResults,
    taskTemplates: categoryResult.libraryActionIds,
    learningContents: categoryResult.learningContentIds,
  };
};

const categoryResultsFrom = (categoryResults) => {
  return categoryResults.map(categoryResultFrom);
};

const reportingResultsFrom = (results, surveyIds) => {
  return {
    overallResult: overallResultFrom(results.overallResult, surveyIds),
    categoryResults: categoryResultsFrom(results.categoryResults),
    learningContents: results.entities.learningContents.map(learningContentFrom),
    taskTemplates: results.entities.libraryActions.map(taskTemplateFrom),
  };
};

const attributeSurveyElementResultFrom = (surveyElementResult, prefix) => {
  const surveyElementPrefix = prefix + surveyElementResult.surveyElementId + '-';
  const attributeResults = surveyElementResult.attributeResults.map((attr) =>
    attributeResultFrom(attr, surveyElementPrefix),
  );

  return {
    id: prefix + surveyElementResult.surveyElementId,
    surveyElement: surveyElementResult.surveyElementId,
    attributeResults,
  };
};

const attributeCategoryResultFrom = (categoryResult) => {
  const categoryPrefix = categoryResult.categoryId + '-';
  const attributeResults = categoryResult.attributeResults.map((attr) => attributeResultFrom(attr, categoryPrefix));
  const surveyElementResults = categoryResult.surveyElementResults.map((surveyElementResult) =>
    attributeSurveyElementResultFrom(surveyElementResult, categoryPrefix),
  );
  return {
    id: categoryResult.categoryId,
    category: categoryResult.categoryId,
    surveyElementResults,
    attributeResults,
  };
};

const attributeReportingResultsFrom = (results) => {
  const overallPrefix = 'overall-';
  return {
    overallAttributeResult: attributeResultFrom(results.overallResult.attributeResults[0], overallPrefix),
    attributeCategoryResults: results.categoryResults.map((result) => attributeCategoryResultFrom(result)),
  };
};

const compareResultFrom = (result) => {
  const isHierarchyAttribute = result.isHierarchyAttribute || false;
  const id = `${result.categoryId}-${result.surveyElementId}-${result.attributeId}-${isHierarchyAttribute}-${result.attributeValueId}`;
  return {
    ...result,
    id,
  };
};

const reportingCompareResultsFrom = (results) => {
  return results.map((result) => compareResultFrom(result));
};

// convert to selectedAttributes to the format accepted by backend
const selectedAttributesFrom = (selectedAttributes) => {
  const attributes = Object.keys(selectedAttributes).map((attributeId) => ({
    id: Number(attributeId),
    values: selectedAttributes[attributeId],
  }));
  if (!attributes.length) return null;
  return attributes;
};

const analyzeCompareDifferKeyFrom = (spec, _type) => {
  switch (spec) {
    case analyzeSpecs.ENPS:
      return 'enpsScoreDiffer';
    case analyzeSpecs.AVG:
      return 'scoreAverageDiffer';
    case analyzeSpecs.PERCENT:
    default:
      return 'scaleFavorableDiffer';
  }
};

const compareResultKeyFrom = (options) => {
  const categoryId = options?.categoryId || undefined;
  const surveyElementId = options?.surveyElementId || undefined;
  const attributeId = options?.attributeId || undefined;
  const isHierarchyAttribute = options?.isHierarchyAttribute || false;
  const attributeValueId = options?.attributeValueId || undefined;
  return `${categoryId}-${surveyElementId}-${attributeId}-${isHierarchyAttribute}-${attributeValueId}`;
};

export {
  analyzeCompareDifferKeyFrom,
  attributeReportingResultsFrom,
  compareResultKeyFrom,
  reportingCompareResultsFrom,
  reportingResultsFrom,
  selectedAttributesFrom,
};
